import { clsx } from 'clsx';
import { Check } from 'lucide-react';
import type { FieldErrors, UseFormRegister, SubmitHandler } from 'react-hook-form';
import type { LeadInput } from '@lead-capture/shared';
import type { ChangeEvent, FocusEvent } from 'react';
import { usePathname } from 'next/navigation';

export type FormServerProps<TFormValues extends LeadInput = LeadInput> = {
  title: string;
  consent: string;

  isDisabled?: boolean;
  isSucceeded?: boolean;
  isFailed?: boolean;

  phoneNumberValue?: string;
  caseTypeValue?: string;
  whereYouInjuredValue?: string;

  register?: UseFormRegister<LeadInput>;
  errors?: FieldErrors<LeadInput>;
  onSubmit?: (e?: React.BaseSyntheticEvent) => void | Promise<void>;

  onCaseTypeChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  onWhereYouInjuredChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onZipCodeBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  SuccessMessage?: React.ComponentType;
  SuccessMessageWithNoPhone?: React.ComponentType;
  FailureMessage?: React.ComponentType;
  FailureMessageWithNoPhone?: React.ComponentType;

  mode?: 'server' | 'client';
};

export function FormServer<TFormValues extends LeadInput>({
  mode = 'server',
  title,
  consent,
  isDisabled,
  isSucceeded,
  isFailed,
  errors,
  onSubmit,
  register,
  onCaseTypeChange,
  onWhereYouInjuredChange,
  onZipCodeBlur,
  onPhoneNumberBlur,
  onPhoneNumberChange,
  SuccessMessage,
  SuccessMessageWithNoPhone,
  FailureMessage,
  FailureMessageWithNoPhone,
  phoneNumberValue,
  caseTypeValue,
  whereYouInjuredValue,
}: FormServerProps<TFormValues>) {
  const pathname = usePathname();

  const pathsWithNoPhone = ['/case-review', '/accident-claim-review'];

  const inputClasses = 'bg-landing-background py-2 px-4 w-full';
  const errorClasses = 'text-red-500 placeholder:text-red-500/50';
  const disabled = mode === 'server' || isDisabled;

  if (isSucceeded) {
    if (pathsWithNoPhone.includes(pathname)) {
      return <SuccessMessageWithNoPhone />;
    }
    return <SuccessMessage />;
  }

  if (isFailed) {
    if (pathsWithNoPhone.includes(pathname)) {
      return <FailureMessageWithNoPhone />;
    }
    return <FailureMessage />;
  }

  return (
    <>
      <h2 className="text-center text-landing-h3 mb-4">{title}</h2>

      <form
        onSubmit={mode === 'client' ? onSubmit : undefined}
        className={clsx(
          'flex flex-col gap-4',
          mode === 'server' && 'pointer-events-none opacity-60'
        )}
      >
        <div className="bg-landing-muted-lightest rounded-2xl overflow-hidden flex flex-col border border-landing-muted-light *:border-b *:border-b-landing-muted-light *:last:border-b-0">
          <label htmlFor="hero-lead.contact.first_name" className="sr-only">
            First name
          </label>
          <input
            id="hero-lead.contact.first_name"
            placeholder="First name"
            type="text"
            {...(mode === 'client' ? register?.('lead.contact.first_name') : {})}
            className={clsx(inputClasses, errors?.lead?.contact?.first_name && errorClasses)}
            title={`First name ${errors?.lead?.contact?.first_name?.message || ''}`}
            disabled={disabled}
          />

          <label htmlFor="hero-lead.contact.last_name" className="sr-only">
            Last name
          </label>
          <input
            id="hero-lead.contact.last_name"
            {...(mode === 'client' ? register?.('lead.contact.last_name') : {})}
            className={clsx(inputClasses, errors?.lead?.contact?.last_name && errorClasses)}
            placeholder="Last name"
            title={`Last name ${errors?.lead?.contact?.last_name?.message || ''}`}
            type="text"
            disabled={disabled}
          />

          <label htmlFor="hero-lead.contact.phone" className="sr-only">
            Phone number
          </label>
          <input
            id="hero-lead.contact.phone"
            className={clsx(inputClasses, errors?.lead?.contact?.phone && errorClasses)}
            placeholder="Phone number"
            title={`Phone number ${errors?.lead?.contact?.phone?.message || ''}`}
            type="text"
            value={phoneNumberValue}
            onChange={mode === 'client' ? onPhoneNumberChange : undefined}
            onBlur={mode === 'client' ? onPhoneNumberBlur : undefined}
            disabled={disabled}
          />

          <label htmlFor="hero-lead.contact.zip_code" className="sr-only">
            ZIP code
          </label>
          <input
            id="hero-lead.contact.zip_code"
            {...(mode === 'client' ? register?.('lead.contact.zip_code') : {})}
            className={clsx(inputClasses, errors?.lead?.contact?.zip_code && errorClasses)}
            onBlur={mode === 'client' ? onZipCodeBlur : undefined}
            placeholder="ZIP code"
            title={`ZIP code ${errors?.lead?.contact?.zip_code?.message || ''}`}
            type="text"
            disabled={disabled}
          />

          <label htmlFor="hero-lead.contact.email" className="sr-only">
            Email
          </label>
          <input
            id="hero-lead.contact.email"
            {...(mode === 'client' ? register?.('lead.contact.email') : {})}
            className={clsx(inputClasses, errors?.lead?.contact?.email && errorClasses)}
            placeholder="Email"
            type="email"
            title={`First name ${errors?.lead?.contact?.email?.message || ''}`}
            disabled={disabled}
          />

          <div className="bg-landing-background py-2 pl-3 pr-4 w-full">
            <label htmlFor="hero-case_type" className="sr-only">
              First name
            </label>
            <select
              id="hero-case_type"
              defaultValue={''}
              onChange={mode === 'client' ? onCaseTypeChange : undefined}
              disabled={disabled}
              className={clsx(
                'w-full ',
                caseTypeValue && caseTypeValue.length
                  ? 'text-landing-primary-dark'
                  : 'text-landing-primary-dark/50',
                errors?.lead?.answers?.['1']?.value?.message && 'text-red-500/50'
              )}
            >
              <option value="" disabled>
                Case type
              </option>
              <option value="1">Auto Accident</option>
              <option value="2">Workplace Accidents</option>
              <option value="3">Medical Malpractice</option>
              <option value="4">Dog Bites/Animal Attack</option>
              <option value="5">Nursing Home Abuse</option>
              <option value="6">Slip and Fall</option>
              <option value="7">Other</option>
            </select>
          </div>

          <div
            className={clsx(
              inputClasses,
              'flex justify-between items-center gap-4',
              errors?.lead?.answers?.['2']?.value?.message && errorClasses
            )}
          >
            <span
              className={clsx(
                (!whereYouInjuredValue || !whereYouInjuredValue.length) && 'opacity-50'
              )}
            >
              Were you injured?
            </span>

            <div className="flex gap-4">
              <div className="flex gap-1">
                <input
                  id="injured-yes"
                  className="
                    appearance-none
                    w-6 h-6
                    border border-landing-primary-dark/50
                    rounded-full
                    bg-white
                    checked:bg-landing-primary-dark
                    checked:border-white
                    hover:cursor-pointer
                  "
                  type="radio"
                  name="injured"
                  value="8"
                  onChange={onWhereYouInjuredChange}
                  disabled={disabled}
                />
                <label
                  htmlFor="injured-yes"
                  className={clsx(
                    (!whereYouInjuredValue || !whereYouInjuredValue.length) && 'opacity-50'
                  )}
                >
                  Yes
                </label>
              </div>
              <div className="flex gap-1">
                <input
                  type="radio"
                  id="injured-no"
                  className="
                    appearance-none
                    w-6 h-6
                    border border-landing-primary-dark/50
                    rounded-full
                    bg-white
                    checked:bg-landing-primary-dark
                    checked:border-white
                    hover:cursor-pointer
                  "
                  name="injured"
                  value="9"
                  onChange={onWhereYouInjuredChange}
                  disabled={disabled}
                />
                <label
                  htmlFor="injured-no"
                  className={clsx(
                    (!whereYouInjuredValue || !whereYouInjuredValue.length) && 'opacity-50'
                  )}
                >
                  No
                </label>
              </div>
            </div>
          </div>

          <label htmlFor="hero-lead.contact.details" className="sr-only">
            Tell us what happened
          </label>
          <textarea
            id="hero-lead.contact.details"
            {...(mode === 'client' ? register?.('lead.contact.details') : {})}
            placeholder="Tell us what happened"
            className={clsx(
              inputClasses,
              'min-h-20',
              errors?.lead?.contact?.details && errorClasses
            )}
            title={`Tell us what happened ${errors?.lead?.contact?.email?.message || ''}`}
            disabled={disabled}
          />
        </div>

        <button
          className="hover:bg-landing-primary-mid transition-colors cursor-pointer bg-landing-primary flex items-center gap-2 justify-center w-full rounded-lg text-landing-body-inverse text-base px-4 py-3"
          disabled={disabled}
        >
          <span>Start Your Claim</span>
          {disabled ? (
            <svg
              className="w-4 h-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                fill="currentColor"
                d="m14.03 8.531-4.5 4.5a.751.751 0 1 1-1.062-1.062l3.22-3.218H2.5a.75.75 0 0 1 0-1.5h9.188L8.469 4.03a.751.751 0 0 1 1.063-1.063l4.5 4.5a.75.75 0 0 1-.001 1.063"
              />
            </svg>
          )}
        </button>

        <div className="flex items-start px-4">
          <div className="relative flex items-center mr-2 mt-1">
            <input
              id="hero-form-consent"
              type="checkbox"
              {...(mode === 'client' ? register?.('lead.contact.consent') : {})}
              className="
                peer
                appearance-none
                w-4 h-4
                border border-[#8a97b0]
                rounded-sm
                bg-white
                checked:bg-landing-primary-dark/50
                cursor-pointer
                transition-all
              "
            />
            <Check
              className="
                absolute
                left-1/2 top-1/2
                -translate-x-1/2 -translate-y-1/2
                w-3 h-3
                text-white
                pointer-events-none
                opacity-0
                peer-checked:opacity-100
                transition-opacity
              "
            />
          </div>

          <label
            htmlFor="hero-form-consent"
            className={clsx(
              'text-landing-small cursor-pointer',
              errors?.lead?.contact?.consent && errorClasses
            )}
            dangerouslySetInnerHTML={{ __html: consent }}
          />
        </div>
      </form>
    </>
  );
}
